import { z } from "zod";

import type {
  ChatCompletionChunk,
  EmbeddingResponsePayload,
  GenerationRequest,
  TextCompletionChunk,
} from "../openai";
import type { ResponseStreamChunk } from "../openai/responses/responses.models";
import type { AliasPricing } from "../services/billing/types/serverless-rates";
import { InferenceModelSchema } from "../lib/models.model";
import { ServerlessRegionSchema } from "@inference-net/cf-compatible-models/serverless-region";
import { AliasPricingSchema } from "../services/billing/types/serverless-rates";
import { GenerationStateSchema } from "./generation-state.models";
import {
  CompletionUsageSchema,
  EmbeddingUsageSchema,
  GenerationRequestSchema,
  GenerationResponseSchema,
} from "../openai";
import {
  authedServiceRequest,
  serviceRequest,
  serviceResponse,
} from "./BaseService";

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

/** Engine generation ids (`generations_v2._id`) carry this prefix. */
export const GENERATION_ID_PREFIX = "gv2_";

export const GENERATION_LANES = ["fast", "slow"] as const;

export const GenerationLaneSchema = z.enum(GENERATION_LANES);

export const GenerationSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  workerId: z.string().nullable(),
  workerUserId: z.string().nullable(),
  workerTeamId: z.string().nullable(),
  instanceId: z.string().nullable(),
  batchId: z.string().nullable(),
  batchRequestId: z.string().nullable(),
  batchCustomId: z.string().nullable(),
  partnerId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  dispatchedAt: z.date().nullable(),
  firstChunkAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  state: GenerationStateSchema,
  stream: z.boolean().optional(),
  stateMessage: z.string(),
  model: InferenceModelSchema.optional(),
  normalizedModel: z.string().nullish(),
  request: GenerationRequestSchema.nullable(),
  response: GenerationResponseSchema.nullable(),
  usage: CompletionUsageSchema.partial(),
  lane: GenerationLaneSchema,
  version: z.number().default(1).nullable(),
});

export type Generation = z.infer<typeof GenerationSchema>;
export type { GenerationState } from "./generation-state.models";
export {
  GENERATION_STATES,
  GenerationStateSchema,
} from "./generation-state.models";
export type GenerationLane = z.infer<typeof GenerationLaneSchema>;

/** ******************************************************************************
 *  Get Generation Result By ID
 ******************************************************************************* */

export const getGenerationResultParams = z.object({
  generationId: z.string(),
});

export const getGenerationResultRequest = authedServiceRequest.merge(
  z.object({
    params: getGenerationResultParams,
  }),
);

/**
 * This represents a Generation business object that is used by our users.
 * This type is safe to return to users and does not leak any internal details.
 */
export const GenerationForClientSchema = z.object({
  id: z.string(),
  state: GenerationStateSchema,
  stateMessage: z.string(),
  request: GenerationRequestSchema.nullable(),
  response: GenerationResponseSchema.nullable(),
  model: z.string(),
  createdAt: z.string(),
  dispatchedAt: z.string().nullable(),
  firstChunkAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  usage: CompletionUsageSchema.or(EmbeddingUsageSchema),
});

/**
 * This represents a Generation business object that is used by our users.
 * This type is safe to return to users and does not leak any internal details.
 */
export type GenerationForClient = z.infer<typeof GenerationForClientSchema>;

/**
 * Admin-facing generation details including internal worker information.
 */
export const GenerationForAdminSchema = GenerationForClientSchema.extend({
  workerId: z.string().nullable(),
  workerTeamId: z.string().nullable(),
});

export type GenerationForAdmin = z.infer<typeof GenerationForAdminSchema>;

export const CreateGenerationAndTransactionsParamsSchema = z.object({
  stream: z.boolean(),
  apiKeyId: z.string().nullable(),
  userId: z.string(),
  model: InferenceModelSchema,
  aliasPricing: AliasPricingSchema,
  // See InternalGeneration.region; absent on messages from older publishers.
  region: ServerlessRegionSchema.nullable().default(null),
  batchId: z.string().nullable(),
  batchRequestId: z.string().nullable(),
  batchCustomId: z.string().nullable(),
  promptTokens: z.number(),
  lane: GenerationLaneSchema,
  partnerId: z.string().nullable(),
  generationId: z.string(),
  canFallbackToEphemeral: z.boolean(),
  shouldAlwaysSkipDb: z.boolean().optional().default(false),
});

export type CreateGenerationAndTransactionsParams = z.infer<
  typeof CreateGenerationAndTransactionsParamsSchema
>;

/**
 * This represents a Generation business object that is used internally by
 * our system.
 *
 * This type is not safe to return to users and may leak internal details.
 */
export const InternalGenerationSchema = z.object({
  _id: z.string(),
  teamId: z.string(),
  userId: z.string(),
  batchId: z.string().nullable(),
  batchRequestId: z.string().nullable(),
  batchCustomId: z.string().nullable(),
  // The API key that created this generation, when known. Populated in-memory
  // at request time (generations_v2 has no column, so a DB-backed row reads
  // back null) purely so the finalization message can carry it to the charge.
  apiKeyId: z.string().nullable(),
  // The project a `/v1/slow` request was made in. Populated in-memory at
  // request time and carried by the slow-lane dispatch and finalization
  // messages (generations_v2 has no column), so finalization can attribute the
  // request's `inference_metadata` row. Null for every other generation.
  projectId: z.string().nullable(),
  workerId: z.string().nullable(),
  workerTeamId: z.string().nullable(),
  instanceId: z.string().nullable(),
  createdAt: z.date(),
  createdAtRaw: z.string(),
  updatedAt: z.date(),
  dispatchedAt: z.date().nullable(),
  firstChunkAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  state: GenerationStateSchema,
  stream: z.boolean().optional(),
  stateMessage: z.string(),
  model: InferenceModelSchema,
  // Alias pricing snapshotted at creation. generations_v2 has no column for
  // it (a DB-backed row reads back null), so it rides the queue messages.
  aliasPricing: AliasPricingSchema,
  // The region whose pinned host the request arrived on (null: global); the
  // charge resolves that region's pricing rules. Stored on generations_v2,
  // and carried by the queue messages for DB-less generations.
  region: ServerlessRegionSchema.nullable(),
  normalizedModel: z.string().nullish(),
  request: GenerationRequestSchema.nullable(),
  response: GenerationResponseSchema.nullable(),
  lane: GenerationLaneSchema,
  partnerId: z.string().nullable(),
  usage: CompletionUsageSchema.partial().nullable(),
  // Parameters required to create the generation later during finalization.
  // If this is set, the generation was not persisted due to DB fallback.
  fallbackCreationParams:
    CreateGenerationAndTransactionsParamsSchema.nullable(),
});

/**
 * This represents a Generation business object that is used internally by
 * our system.
 *
 * This type is not safe to return to users and may leak internal details.
 */
export type InternalGeneration = z.infer<typeof InternalGenerationSchema>;

export const getGenerationResultResponse = serviceResponse.merge(
  GenerationForClientSchema,
);

export type GetGenerationResultParams = z.infer<
  typeof getGenerationResultParams
>;
export type GetGenerationResultRequest = z.infer<
  typeof getGenerationResultRequest
>;
export type GetGenerationResultResponse = z.infer<
  typeof getGenerationResultResponse
>;

/** ******************************************************************************
 *  Get Generation By ID
 ******************************************************************************* */

export const getGenerationByIdParams = z.object({
  generationId: z.string(),
});

export const getGenerationByIdRequest = authedServiceRequest.merge(
  z.object({
    params: getGenerationByIdParams,
  }),
);

export const getGenerationByIdResponse = serviceResponse.merge(
  z.object({
    generation: GenerationForClientSchema.optional(),
  }),
);

export type GetGenerationByIdParams = z.infer<typeof getGenerationByIdParams>;
export type GetGenerationByIdRequest = z.infer<typeof getGenerationByIdRequest>;
export type GetGenerationByIdResponse = z.infer<
  typeof getGenerationByIdResponse
>;

// New admin-specific Get Generation By ID response --------------------------------
export const getGenerationByIdForAdminResponse = serviceResponse.merge(
  z.object({
    generation: GenerationForAdminSchema.optional(),
  }),
);

/**
 * Response shape for super-admin GetGenerationById endpoint which returns
 * a GenerationForAdmin object (includes internal worker metadata).
 */
export type GetGenerationByIdForAdminResponse = z.infer<
  typeof getGenerationByIdForAdminResponse
>;

/** ******************************************************************************
 *  Successful Generation Persistence Types
 ******************************************************************************* */

/**
 * Base fields required to persist a successful generation result.
 *
 * Phase-3 billing-v2: the v2 charge that settles the spend is recorded
 * separately by `BillingServiceV2.createCharge`, and the stream-handler
 * threads a shared `TransactionContext` through both writes so they
 * commit (or roll back) atomically. See
 * `generation.stream-handlers.service.ts:finalizeSuccessfulGeneration`.
 */
export type SaveSuccessfulGenerationParams = {
  generationId: string;
  /**
   * Null when the reported instance can't be persisted — e.g. a
   * deployment generation whose instance id no longer resolves
   * (`generations_v2.instance_id` FKs `deployment_instances`).
   */
  instanceId: string | null;
  promptTokens: number;
  completionTokens: number;
  dispatchedAt: Date;
  finishedAt: Date;
  firstChunkAt: Date | null;
};

/** ******************************************************************************
 *  Failed Generation Persistence Types
 ******************************************************************************* */

export type SaveFailedGenerationParams = {
  generationId: string;
  errorMessage: string;
  instanceId: string | null;
  promptTokens: number | null;
  dispatchedAt: Date | null;
  finishedAt: Date;
  firstChunkAt: Date | null;
};

/** ******************************************************************************
 *  Cancelled Generation Persistence Types
 ******************************************************************************* */

/**
 * When the cancelled generation carries a partial charge, the caller threads
 * a shared `TransactionContext` through the state flip and the charge, same
 * as the Success path. Token counts are null when the client disconnected
 * before the first usage-bearing chunk.
 */
export type SaveCancelledGenerationParams = {
  generationId: string;
  instanceId: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  dispatchedAt: Date | null;
  finishedAt: Date;
  firstChunkAt: Date | null;
};

/** ******************************************************************************
 *  List Generations By User
 ******************************************************************************* */

export const listGenerationsResponse = serviceResponse.merge(
  z.object({
    generations: z.array(GenerationForClientSchema).optional(),
    total: z.number().optional(),
  }),
);
export type ListGenerationsResponse = z.infer<typeof listGenerationsResponse>;

/** ******************************************************************************
 *  Count Generations
 ******************************************************************************* */

export const countGenerationsParams = z.object({
  state: GenerationStateSchema,
});

export const countGenerationsRequest = serviceRequest.merge(
  z.object({
    params: countGenerationsParams,
  }),
);

export const countGenerationsResponse = serviceResponse.merge(
  z.object({
    count: z.number().optional(),
  }),
);

export type CountGenerationsParams = z.infer<typeof countGenerationsParams>;
export type CountGenerationsRequest = z.infer<typeof countGenerationsRequest>;
export type CountGenerationsResponse = z.infer<typeof countGenerationsResponse>;

/** ******************************************************************************
 *  RPS
 ******************************************************************************* */

export const generationRPMParams = z.object({
  state: GenerationStateSchema,
});

export const generationRPMRequest = serviceRequest.merge(
  z.object({
    params: generationRPMParams,
  }),
);

export const generationRPMResponse = serviceResponse.merge(
  z.object({
    rpm: z.number().optional(),
  }),
);

export type GenerationRPMParams = z.infer<typeof generationRPMParams>;
export type GenerationRPMRequest = z.infer<typeof generationRPMRequest>;
export type GenerationRPMResponse = z.infer<typeof generationRPMResponse>;

/**
 * Payload types sent from a worker to the server for a generation
 */
export type GenerationResponsePayload =
  | InferenceResponseChunk
  | GenerationResponseError
  | GenerationResponseLock
  | GenerationResponseEmbedding;

export type InferenceResponseChunk = {
  msgType: "CHUNK";
  chunk: ChatCompletionChunk | TextCompletionChunk | ResponseStreamChunk;
  i: number;
};

export type GenerationResponseError = {
  msgType: "ERROR";
  errorMessage: string;
};

/**
 * Sticky-routing result the elixir engine reports on the LOCK event of a
 * generation dispatched with `stickyRouting: true`.
 *
 * - `hit`: routed to the instance that served the matching message prefix
 * - `miss`: no prefix match; routed normally and the prefix was recorded
 * - `fallback`: the matched instance was unavailable; routed elsewhere
 * - `disabled`: the engine declined sticky routing (see `reason`)
 */
export const StickyRoutingOutcomeSchema = z.object({
  outcome: z.enum(["hit", "miss", "fallback", "disabled"]),
  /** How many prefix messages matched; set on hit/fallback. */
  matchedDepth: z.number().optional(),
  /** Total messages the engine hashed for the lookup. */
  messageCount: z.number().optional(),
  reason: z.string().optional(),
});
export type StickyRoutingOutcome = z.infer<typeof StickyRoutingOutcomeSchema>;

export type GenerationResponseLock = {
  msgType: "LOCK";
  instanceId: string;
  /** Present only when the request was dispatched with `stickyRouting: true`. */
  sticky?: StickyRoutingOutcome;
};

export type GenerationResponseEmbedding = {
  msgType: "EMBEDDING_RESULT";
  result: EmbeddingResponsePayload;
};

export type SubmitSlowGeneration = {
  /**
   * @deprecated Use generationId instead
   */
  id: string;
  generationId: string;
  input: GenerationRequest;
  generationRequestUrl: string | null;
  batchId: string | null;
  /** Epoch ms — hard deadline after which the generation should be failed. */
  expiresAt: number | null;
  /** See InternalGeneration.aliasPricing; absent bills list price. */
  aliasPricing?: AliasPricing;
  /** See InternalGeneration.projectId; absent on messages from older publishers. */
  projectId?: string | null;
  /** The generation's model; absent on messages from older publishers. */
  model?: string;
};

// Zod schemas for generation results
export const SuccessfulGenerationResultSchema = z.object({
  generationId: z.string(),
  response: GenerationResponseSchema,
  instanceId: z.string(),
  dispatchedAt: z.number(),
  finishedAt: z.number(),
  firstChunkAt: z.number().nullable(),
  batchId: z.string().nullable(),
  // API key that created the generation, when known. Rides the message because
  // generations_v2 has no api-key column; the finalization charge attributes
  // the usage-by-API-key rollup from this. Null for batch/legacy generations.
  apiKeyId: z.string().nullable(),
  // See InternalGeneration.aliasPricing; absent bills list price.
  aliasPricing: AliasPricingSchema.optional(),
  // See InternalGeneration.region; absent bills global prices.
  region: ServerlessRegionSchema.nullable().optional(),
  // See InternalGeneration.projectId; absent on messages from older publishers.
  projectId: z.string().nullable().optional(),
  creationParams: CreateGenerationAndTransactionsParamsSchema.nullable(),
});

export type SuccessfulGenerationResult = z.infer<
  typeof SuccessfulGenerationResultSchema
>;

/**
 * Who authored `errorMessage`: `internal` for text we wrote ourselves,
 * `upstream` for text forwarded from an inference engine. Engine text quotes
 * the request often enough that it counts as customer content, so only
 * `internal` messages survive for teams that disabled inference-results
 * retention.
 */
export const ErrorMessageSourceSchema = z.enum(["internal", "upstream"]);

export type ErrorMessageSource = z.infer<typeof ErrorMessageSourceSchema>;

export const FailedGenerationResultSchema = z.object({
  generationId: z.string(),
  errorMessage: z.string(),
  // Messages published before this field existed are still in flight during a
  // rolling deploy; treating them as upstream keeps the retention gate closed
  // rather than leaking engine text for a zero-retention team.
  errorMessageSource: ErrorMessageSourceSchema.default("upstream"),
  instanceId: z.string().nullable(),
  dispatchedAt: z.number().nullable(),
  finishedAt: z.number(),
  firstChunkAt: z.number().nullable(),
  batchId: z.string().nullable(),
  // Mirrors SuccessfulGenerationResultSchema.apiKeyId so the union stays
  // coherent; failed generations don't charge, but the field parses the same.
  apiKeyId: z.string().nullable(),
  // See InternalGeneration.projectId; absent on messages from older publishers.
  projectId: z.string().nullable().optional(),
  creationParams: CreateGenerationAndTransactionsParamsSchema.nullable(),
});

export type FailedGenerationResult = z.infer<
  typeof FailedGenerationResultSchema
>;

/**
 * Finalization message for a client-cancelled generation on a deployment with
 * `enable_incremental_usage_billing`. Carries the last cumulative usage the
 * router saw before the abort (per-chunk usage from the engine's
 * `continuous_usage_stats`); null when the client disconnected before the
 * first usage-bearing chunk — the consumer then flips the state without a
 * charge. The `kind` literal is what distinguishes it in the finalization
 * union: the success/failed members are told apart structurally
 * (`"errorMessage" in result`) and carry no discriminator to add one to.
 */
export const CancelledGenerationResultSchema = z.object({
  kind: z.literal("cancelled"),
  generationId: z.string(),
  usage: CompletionUsageSchema.nullable(),
  instanceId: z.string().nullable(),
  dispatchedAt: z.number().nullable(),
  finishedAt: z.number(),
  firstChunkAt: z.number().nullable(),
  batchId: z.string().nullable(),
  // Mirrors SuccessfulGenerationResultSchema.apiKeyId: attributes the partial
  // charge's usage-by-API-key rollup.
  apiKeyId: z.string().nullable(),
  // See InternalGeneration.aliasPricing; prices the partial charge.
  aliasPricing: AliasPricingSchema.optional(),
  // See InternalGeneration.region; absent bills global prices.
  region: ServerlessRegionSchema.nullable().optional(),
  creationParams: CreateGenerationAndTransactionsParamsSchema.nullable(),
  // See InternalGeneration.projectId; absent on messages from older publishers.
  projectId: z.string().nullable().optional(),
});

export type CancelledGenerationResult = z.infer<
  typeof CancelledGenerationResultSchema
>;

export type BatchInfo = {
  batchId: string;
  requestId: string;
  customId: string;
  batchSize: number;
};

// Union schemas for finalization messages
export const FinalizationMessageSchema = z.union([
  SuccessfulGenerationResultSchema,
  FailedGenerationResultSchema,
  CancelledGenerationResultSchema,
]);

export type FinalizationMessage = z.infer<typeof FinalizationMessageSchema>;

// Slow group finished message schema
export const SlowGroupFinishedMessageSchema = z.object({
  groupId: z.string(),
  groupSize: z.number(),
  finalizedAt: z.number(),
  metadata: z.record(z.string(), z.any()).optional(),
  webhookId: z.string().optional(),
  teamId: z.string(),
  /** How the group ended. Absent on messages published before this field existed, which were all completions. */
  status: z.enum(["completed", "failed"]).optional(),
});

export type SlowGroupFinishedMessage = z.infer<
  typeof SlowGroupFinishedMessageSchema
>;

// Slow dispatcher submission message schema
export const SlowDispatcherSubmissionMessageSchema = z.object({
  id: z.string(), // Deprecated, use generationId
  generationId: z.string(),
  input: GenerationRequestSchema,
  generationRequestUrl: z.string().nullable(),
  batchId: z.string().nullable(),
});

export type SlowDispatcherSubmissionMessage = z.infer<
  typeof SlowDispatcherSubmissionMessageSchema
>;

// NATS JetStream max deliveries advisory message schema
export const MaxDeliveriesAdvisoryMessageSchema = z.object({
  type: z.string(),
  id: z.string(),
  timestamp: z.string(),
  stream: z.string(),
  consumer: z.string(),
  stream_seq: z.number(),
  deliveries: z.number(),
  domain: z.string().optional(),
});

export type MaxDeliveriesAdvisoryMessage = z.infer<
  typeof MaxDeliveriesAdvisoryMessageSchema
>;

// Wrapped advisory message that includes both advisory data and original message
export const MaxDeliveriesAdvisoryWrapperSchema = z.object({
  advisory: MaxDeliveriesAdvisoryMessageSchema,
  originalMessage: z.unknown(), // Will be typed based on the watched consumer's routes
});

export type MaxDeliveriesAdvisoryWrapper = z.infer<
  typeof MaxDeliveriesAdvisoryWrapperSchema
>;

// JetStream stream for dispatching inference requests to workers

// Fields shared by every finalized-generation event. The lightweight event is
// exactly this base (+ a precomputed `stream` flag); the heavy webhook events
// add the request and the response/errorMessage payload.
const FinalizedGenerationBaseSchema = z.object({
  generationId: z.string(),
  batchId: z.string().nullable(),
  finalizedAt: z.number(),
  lane: GenerationLaneSchema,
  webhookId: z.string().optional(),
});

export const SuccessfullyFinalizedGenerationMessageSchema =
  FinalizedGenerationBaseSchema.extend({
    request: GenerationRequestSchema.nullable(), // TODO: Should never be null
    response: GenerationResponseSchema.nullable(),
  });

export type SuccessfullyFinalizedGenerationMessage = z.infer<
  typeof SuccessfullyFinalizedGenerationMessageSchema
>;

export const FailedFinalizedGenerationMessageSchema =
  FinalizedGenerationBaseSchema.extend({
    request: GenerationRequestSchema.nullable(), // TODO: Should never be null
    errorMessage: z.string(),
  });

export type FailedFinalizedGenerationMessage = z.infer<
  typeof FailedFinalizedGenerationMessageSchema
>;

export type FinalizedGenerationMessage =
  | SuccessfullyFinalizedGenerationMessage
  | FailedFinalizedGenerationMessage;

/**
 * Lightweight finalized-generation event: the shared base plus the few derived
 * flags downstream consumers need, with NO request/response payload. Consumed
 * by the analytics, cleanup, and batch handlers — none of them reads the
 * payload, so carrying it would be pure overhead (and, at the finalization
 * consumer's prefetch, the dominant source of resident memory). The webhooks consumer keeps receiving the heavy
 * {@link FinalizedGenerationMessage} via its own routing key.
 */
export const FinalizedGenerationLightMessageSchema =
  FinalizedGenerationBaseSchema.extend({
    /**
     * Precomputed `request.stream` for analytics (undefined for embeddings).
     * Carried here so analytics never has to deserialize the request body.
     */
    stream: z.boolean().optional(),
    /**
     * Owning team, so the cleanup consumer can resolve retention without
     * reading the generation row. Absent on events from publishers that do
     * not set it; consumers fall back to reading the row.
     */
    teamId: z.string().optional(),
  });

export type FinalizedGenerationLightMessage = z.infer<
  typeof FinalizedGenerationLightMessageSchema
>;

/** The `/v1/slow` endpoints whose requests get an `inference_metadata` row at finalization. */
export const SlowRequestPathSchema = z.enum([
  "/v1/slow/chat/completions",
  "/v1/slow/completions",
  "/v1/slow/responses",
]);

export type SlowRequestPath = z.infer<typeof SlowRequestPathSchema>;

/**
 * A finalized `/v1/slow` generation. Published after the finalization commit,
 * and again on every redelivery of the finalization message, so consumers
 * must be idempotent on `generationId`. llm-ops-consumer writes the request's
 * `inference_metadata` row from it, reading the generation and its charge back
 * from Postgres so the row carries exactly what was billed.
 */
export const SlowRequestFinalizedMessageSchema = z.object({
  generationId: z.string(),
  projectId: z.string(),
  requestPath: SlowRequestPathSchema,
});

export type SlowRequestFinalizedMessage = z.infer<
  typeof SlowRequestFinalizedMessageSchema
>;

export const isSuccessfullyFinalizedGenerationMessage = (
  message: FinalizedGenerationMessage,
): message is SuccessfullyFinalizedGenerationMessage => {
  return !("errorMessage" in message);
};

export const isFailedGenerationResult = (
  result: SuccessfulGenerationResult | FailedGenerationResult,
): result is FailedGenerationResult => {
  return "errorMessage" in result;
};

export const isCancelledGenerationResult = (
  result: FinalizationMessage,
): result is CancelledGenerationResult => {
  // Explicit kind match (widened to string so a future second `kind` member
  // keeps this guard honest); success/failed are told apart structurally and
  // carry no discriminator.
  const kind: string | undefined = "kind" in result ? result.kind : undefined;
  return kind === "cancelled";
};
